import React from "react";
import { usePostHog } from "posthog-js/react";
import { trackError } from "@/utils/posthog";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { isSessionError } from "@/utils/authErrorHandler";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    // Track error in PostHog
    // Note: We can't use hooks in class components, so we'll emit a custom event
    // that the ErrorTracker component will listen for
    window.dispatchEvent(
      new CustomEvent("uncaught-error", {
        detail: { error, errorInfo, timestamp: Date.now() },
      }),
    );

    console.error("Uncaught error:", error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <this.props.fallback error={this.state.error!} resetError={this.resetError} />;
      }

      return <DefaultErrorFallback error={this.state.error!} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

// Default error fallback component
const DefaultErrorFallback: React.FC<{ error: Error; resetError: () => void }> = ({
  error,
  resetError,
}) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-xs space-y-8">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100/80 ring-8 ring-red-50/50">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {t("errorBoundary.title")}
            </h1>
            <p className="text-sm text-muted-foreground max-w-[280px] mx-auto">
              {t("errorBoundary.description")}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {import.meta.env.DEV && (
            <div className="rounded-lg bg-red-50/50 border border-red-100 p-4">
              <p className="text-xs font-semibold text-red-800 uppercase tracking-wider mb-2">
                {t("errorBoundary.debugInfo")}
              </p>
              <div className="overflow-auto max-h-[120px] rounded bg-white/50 p-2">
                <p className="text-xs text-red-600 font-mono break-all">
                  {error.name}: {error.message}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3 pt-2">
            <Button onClick={resetError} className="w-full" size="lg">
              <RefreshCw className="mr-2 h-4 w-4" />
              {t("errorBoundary.tryAgain")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => window.location.reload()}
              className="w-full"
              size="lg"
            >
              {t("errorBoundary.reloadPage")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Component to track uncaught errors with PostHog
export const ErrorTracker: React.FC = () => {
  const posthog = usePostHog();
  const { user } = useAuth();

  React.useEffect(() => {
    const handleUncaughtError = (event: CustomEvent) => {
      const { error, errorInfo } = event.detail;

      // Don't track auth/session errors - these are expected during
      // account switching or when sessions become stale
      if (isSessionError(error)) {
        console.info("Ignoring session error in error tracking:", error?.message);
        return;
      }

      trackError(posthog, error, {
        component: "ErrorBoundary",
        action: "uncaught_error",
        user_id: user?.id,
        component_stack: errorInfo?.componentStack?.slice(0, 500),
        error_boundary: true,
      });
    };

    // Also track unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

      // Don't track auth/session errors - these are expected during
      // account switching or when sessions become stale
      if (isSessionError(event.reason) || isSessionError(error)) {
        console.info("Ignoring session error in rejection tracking:", error?.message);
        return;
      }

      trackError(posthog, error, {
        component: "window",
        action: "unhandled_promise_rejection",
        user_id: user?.id,
        error_boundary: false,
      });
    };

    window.addEventListener("uncaught-error", handleUncaughtError as EventListener);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("uncaught-error", handleUncaughtError as EventListener);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, [posthog, user?.id]);

  return null;
};
